/** GamePlatPlay content catalog — five clusters, 100 reserved URLs.

Funnel we are actually trying to win:
  Google search → GamePlatPlay guide → player → account/subscriber → contestant → returning user

We use the question→answer→internal-link workflow. We do not chase
"$40,000 traffic value." Launch indexes the first 30 Google targets only.
Queued slugs stay noindex until Search Console demand and a human review.
*/
export const SITE = "https://gameplatplay.com";

export const CLUSTERS = [
  { id: "academy", name: "Balatro Beginner Academy", target: 20 },
  { id: "jokers", name: "Joker Database & Guides", target: 30 },
  { id: "decks", name: "Deck Guides", target: 15 },
  { id: "strategy", name: "Strategy / Questions", target: 25 },
  { id: "tournament", name: "Tournament content", target: 10 },
];

export const WAITLIST = { label: "Join the Balatro tournament waitlist", to: "/tournaments" };
export const ACCOUNT = { label: "Create a free GamePlat account", to: "/login" };
export const YORICK = { label: "Ask Yorick this question", to: "/chat?game=balatro" };

const FUNNEL_LINKS = ["/balatro", "/tournaments", "/login"];

function launch(row) {
  return {
    kind: "guide",
    status: "launch",
    indexable: true,
    wave: "first-30",
    intent: row.intent || "informational",
    cta: WAITLIST,
    secondaryCta: ACCOUNT,
    links: row.links || FUNNEL_LINKS,
    ...row,
  };
}

function queued(cluster, query, slug, title, outline, intent = "informational") {
  return {
    kind: "guide",
    status: "queued",
    indexable: false,
    wave: "demand",
    cluster,
    query,
    path: `/balatro/${slug}`,
    title,
    h1: title.replace(" | GamePlat", ""),
    description: `${outline} Join GamePlat's Balatro community.`.slice(0, 158),
    intent,
    outline,
    cta: WAITLIST,
    secondaryCta: ACCOUNT,
    links: FUNNEL_LINKS,
  };
}

const ACADEMY_LAUNCH = [
  launch({
    cluster: "academy",
    query: "Balatro beginner guide",
    path: "/balatro/beginner-guide",
    title: "Balatro Beginner Guide | GamePlat",
    h1: "Balatro beginner guide",
    description: "A beginner Balatro guide: blinds, Jokers, economy, and your first Ante 8. Then join GamePlat's free community tournament waitlist.",
    answer: "Balatro is a single-player poker roguelike. You beat chip targets (blinds) by playing poker hands while Jokers rewrite the score. A first win is not about fancy legendaries — it is economy, one hand to scale, and two Jokers that still work next ante. GamePlat is the community layer on top of that: accounts, a waitlist, and a fair contest around your own runs.",
    bullets: [
      "Start on Red Deck, white stake. Extra discard is forgiveness.",
      "Pick one poker hand to planet. Mixing every hand starves the run.",
      "Hold $25 for the $5 interest cap when you can.",
      "When you can clear Ante 8, keep a score screenshot for GamePlat verification.",
    ],
    faq: [
      { q: "Do I need GamePlat to play Balatro?", a: "No. Buy Balatro from an official store. GamePlat is tournaments, Yorick, and the player community." },
    ],
    links: ["/balatro/how-to-play", "/balatro/strategy-for-beginners", "/balatro/how-tournaments-work", "/tournaments"],
  }),
  launch({
    cluster: "academy",
    query: "How to play Balatro",
    path: "/balatro/how-to-play",
    faq: [{ q: "How to play Balatro", a: "Each round you play or discard poker-style hands against a chip blind. Beat Small, Big, then Boss to clear an ante. Between rounds the shop sells Jokers, packs, and vouchers. The run ends when a blind beats you \u2014 or when you defeat the Ante 8 boss. You are not playing online poker against other people inside a run." }],
    title: "How to Play Balatro | GamePlat",
    h1: "How to play Balatro",
    description: "How Balatro rounds work: hands, discards, shops, antes, and Jokers. Built for first-time players heading toward GamePlat contests.",
    answer: "Each round you play or discard poker-style hands against a chip blind. Beat Small, Big, then Boss to clear an ante. Between rounds the shop sells Jokers, packs, and vouchers. The run ends when a blind beats you — or when you defeat the Ante 8 boss. You are not playing online poker against other people inside a run.",
    bullets: [
      "Play a hand to score. Discard to reshape the next draw.",
      "Score is chips × mult. Planets raise a hand; Jokers modify the formula.",
      "Antes 1–3 buy a direction. Antes 4–8 require an engine.",
      "Community contests use your single-player score, verified on GamePlat.",
    ],
    links: ["/balatro/beginner-guide", "/balatro/how-jokers-work", "/balatro/scoring", "/tournaments"],
  }),
  launch({
    cluster: "academy",
    query: "Balatro strategy for beginners",
    path: "/balatro/strategy-for-beginners",
    faq: [{ q: "Balatro strategy for beginners", a: "Beginners stall at Ante 4\u20135 because they bought interesting Jokers instead of scoring Jokers. Early you need chips so blinds do not kill you, then +Mult so each hand jumps, then \u00d7Mult so Ante 8 is possible. Skip a bad shop. One engine beats five half-engines." }],
    title: "Balatro Strategy for Beginners | GamePlat",
    h1: "Balatro strategy for beginners",
    description: "Beginner Balatro strategy that wins runs: +chips, +Mult, interest, and Joker selection — the same advice players still ask in 2026.",
    answer: "Beginners stall at Ante 4–5 because they bought interesting Jokers instead of scoring Jokers. Early you need chips so blinds do not kill you, then +Mult so each hand jumps, then ×Mult so Ante 8 is possible. Skip a bad shop. One engine beats five half-engines.",
    bullets: [
      "If blinds are close, buy chips or a cheaper hand you can actually make.",
      "Green Joker, Ride the Bus, and Supernova teach scaling without a 20-step combo.",
      "Do not force Flush on a deck that is not suited for it.",
      "Screenshot wins. GamePlat verifies contest scores with proof.",
    ],
    links: ["/balatro/past-ante-4", "/balatro/plus-mult-vs-xmult", "/balatro/best-jokers-for-beginners", "/tournaments"],
  }),
  launch({
    cluster: "academy",
    query: "How do Jokers work in Balatro?",
    path: "/balatro/how-jokers-work",
    faq: [{ q: "How do Jokers work in Balatro?", a: "Jokers sit in slots and change every hand you play. Some add chips, some add Mult, some multiply Mult, some retrigger cards, some pay money. They are not poker wildcards. Reading the Joker text and placing copies next to the right neighbor is most of mid-game skill." }],
    title: "How Do Jokers Work in Balatro? | GamePlat",
    h1: "How do Jokers work in Balatro?",
    description: "Jokers are the scoring engine in Balatro: +chips, +Mult, ×Mult, copies, and economy. Learn the types before you buy the shop.",
    answer: "Jokers sit in slots and change every hand you play. Some add chips, some add Mult, some multiply Mult, some retrigger cards, some pay money. They are not poker wildcards. Reading the Joker text and placing copies next to the right neighbor is most of mid-game skill.",
    bullets: [
      "+Chips help you not die. +Mult raises the floor. ×Mult wins Ante 8.",
      "Blueprint copies the Joker to its right. Brainstorm copies the left.",
      "Selling a Joker is allowed — Campfire and some tags even want it.",
      "Contest play: legal Jokers only. No injected or modded Jokers.",
    ],
    links: ["/balatro/how-to-order-jokers", "/balatro/best-jokers-for-beginners", "/balatro/plus-mult-vs-xmult", "/tournaments"],
  }),
  launch({
    cluster: "academy",
    query: "Best Balatro deck for beginners",
    path: "/balatro/best-deck-for-beginners",
    faq: [{ q: "Best Balatro deck for beginners", a: "Red Deck is the best first deck: one extra discard is extra chances to find the hand you actually scaled. Yellow Deck is the money deck if you already understand the $25 interest cap. Blue Deck's extra hand is power with less flexibility. Do not start on Black, Ghost, or Plasma." }],
    title: "Best Balatro Deck for Beginners | GamePlat",
    h1: "Best Balatro deck for beginners",
    description: "Red Deck is the beginner default in Balatro. Why extra discard wins first runs — and how Yellow/Blue compare.",
    answer: "Red Deck is the best first deck: one extra discard is extra chances to find the hand you actually scaled. Yellow Deck is the money deck if you already understand the $25 interest cap. Blue Deck's extra hand is power with less flexibility. Do not start on Black, Ghost, or Plasma.",
    bullets: [
      "Red: extra discard. Forgiveness while you learn shops.",
      "Yellow: starting gold. Only if you will not dump it on packs.",
      "Blue: extra hand. More scoring, fewer discards.",
      "Unlock other decks by winning — then come back for deck guides.",
    ],
    links: ["/balatro/red-deck", "/balatro/how-to-unlock-decks", "/balatro/beginner-guide", "/tournaments"],
  }),
  launch({
    cluster: "academy",
    query: "How to beat Balatro",
    path: "/balatro/how-to-beat-balatro",
    faq: [{ q: "How to beat Balatro", a: "A win is Ante 8, not a flashy shop. Build interest, commit to a hand by Ante 4, and make sure chips and Mult both exist before you stack \u00d7Mult. If your score is still one raw Full House, the final boss will end the run." }],
    title: "How to Beat Balatro (Ante 8) | GamePlat",
    h1: "How to beat Balatro",
    description: "Beating Balatro means clearing the Ante 8 boss. Economy, one scaled hand, and an ×Mult engine — then a verified GamePlat score.",
    answer: "A win is Ante 8, not a flashy shop. Build interest, commit to a hand by Ante 4, and make sure chips and Mult both exist before you stack ×Mult. If your score is still one raw Full House, the final boss will end the run.",
    bullets: [
      "White stake first. Gold stake is a later problem.",
      "Solve the boss blind rule (discards, face cards, hand size) before greed.",
      "Keep the seed and a final-score screenshot for contests.",
    ],
    links: ["/balatro/past-ante-8", "/balatro/how-to-play", "/balatro/how-tournaments-work", "/tournaments"],
  }),
  launch({
    cluster: "academy",
    query: "How to unlock Jokers in Balatro",
    path: "/balatro/how-to-unlock-jokers",
    faq: [{ q: "How to unlock Jokers in Balatro", a: "The collection fills as you encounter Jokers in shops, packs, and events during real runs. Some Jokers hide behind challenges or stake wins. There is no GamePlat unlock store \u2014 play the official game, then use Yorick when you want a plan for what you already have." }],
    title: "How to Unlock Jokers in Balatro | GamePlat",
    h1: "How to unlock Jokers in Balatro",
    description: "Most Balatro Jokers unlock by discovering them in runs. Challenges and stakes unlock the rest. You do not buy Jokers on GamePlat.",
    answer: "The collection fills as you encounter Jokers in shops, packs, and events during real runs. Some Jokers hide behind challenges or stake wins. There is no GamePlat unlock store — play the official game, then use Yorick when you want a plan for what you already have.",
    bullets: [
      "See a Joker in a run → it can appear again in the collection.",
      "Challenges and higher stakes gate some of the weird ones.",
      "Mods that unlock everything disqualify GamePlat contest play.",
    ],
    links: ["/balatro/how-to-unlock-decks", "/balatro/challenge-mode", "/balatro/best-jokers", "/tournaments"],
  }),
  launch({
    cluster: "academy",
    query: "How to unlock decks in Balatro",
    path: "/balatro/how-to-unlock-decks",
    faq: [{ q: "How to unlock decks in Balatro", a: "Red Deck is yours from the start. Winning a run on a deck unlocks the next one in the list. A handful of decks are challenge rewards. If you are still unlocking, stay on Red until Ante 8 is comfortable \u2014 that is also the skill floor for GamePlat events." }],
    title: "How to Unlock Decks in Balatro | GamePlat",
    h1: "How to unlock decks in Balatro",
    description: "Win a Balatro run to unlock the next deck in the collection. Challenges unlock a few more. Red Deck is available immediately.",
    answer: "Red Deck is yours from the start. Winning a run on a deck unlocks the next one in the list. A handful of decks are challenge rewards. If you are still unlocking, stay on Red until Ante 8 is comfortable — that is also the skill floor for GamePlat events.",
    bullets: [
      "Win with the current deck to unlock the next.",
      "Challenges sit in their own menu and reward specific decks.",
      "New decks change discards, money, or Joker slots — read the sticker.",
    ],
    links: ["/balatro/best-deck-for-beginners", "/balatro/red-deck", "/balatro/challenge-mode", "/tournaments"],
  }),
  launch({
    cluster: "academy",
    query: "Balatro challenge mode guide",
    path: "/balatro/challenge-mode",
    faq: [{ q: "Balatro challenge mode guide", a: "Challenge mode is a set of constrained runs inside Balatro (banned Jokers, weird decks, forced rules). They are great practice and they unlock content. They are not the GamePlat Community Challenge. Do not submit a challenge-modified run to a standard-deck contest unless the event says so." }],
    title: "Balatro Challenge Mode Guide | GamePlat",
    h1: "Balatro challenge mode guide",
    description: "Balatro challenges are seeded rule-sets, not the community tournament. Learn them separately from GamePlat fair-play events.",
    answer: "Challenge mode is a set of constrained runs inside Balatro (banned Jokers, weird decks, forced rules). They are great practice and they unlock content. They are not the GamePlat Community Challenge. Do not submit a challenge-modified run to a standard-deck contest unless the event says so.",
    bullets: [
      "Play challenges in Balatro to unlock decks and Jokers.",
      "GamePlat contests use the event's posted rules — usually a standard run.",
      "Ask Yorick for a challenge line; submit scores on the tournament page.",
    ],
    links: ["/balatro/how-tournaments-work", "/balatro/fair-play", "/balatro/how-to-unlock-jokers", "/tournaments"],
  }),
];

const JOKER_LAUNCH = [
  launch({
    cluster: "jokers",
    query: "Best Balatro Jokers",
    path: "/balatro/best-jokers",
    faq: [{ q: "Best Balatro Jokers", a: "The 'best' Joker is the one that multiplies what you already do. Blueprint and Brainstorm are best when they copy a real payload. Photograph is best with retriggers. A legendary you cannot trigger is worse than Green Joker. For first wins, use the beginner list. For contests, bring a coherent engine and proof." }],
    title: "Best Balatro Jokers | GamePlat",
    h1: "Best Balatro Jokers",
    description: "There is no single best Joker. The best Balatro Jokers complete an engine: copies, retriggers, and ×Mult. Beginners should start elsewhere.",
    answer: "The 'best' Joker is the one that multiplies what you already do. Blueprint and Brainstorm are best when they copy a real payload. Photograph is best with retriggers. A legendary you cannot trigger is worse than Green Joker. For first wins, use the beginner list. For contests, bring a coherent engine and proof.",
    bullets: [
      "Copies (Blueprint / Brainstorm) scale whatever is already winning.",
      "Retriggers multiply Photograph, face cards, and glass.",
      "Economy Jokers are best when interest is not already capped.",
      "Fair play: no save-editors stuffing legendaries into a contest seed.",
    ],
    links: ["/balatro/best-jokers-for-beginners", "/balatro/best-joker-combinations", "/balatro/how-to-order-jokers", "/tournaments"],
  }),
  launch({
    cluster: "jokers",
    query: "Best Balatro Jokers for beginners",
    path: "/balatro/best-jokers-for-beginners",
    faq: [{ q: "Best Balatro Jokers for beginners", a: "Buy Jokers that score even when your poker is messy. Green Joker and Ride the Bus add Mult for consistent habits. Supernova rewards spamming one hand. Photograph plus Hanging Chad is the first combo worth learning. Skip highly conditional Jokers until shops are readable." }],
    title: "Best Balatro Jokers for Beginners | GamePlat",
    h1: "Best Balatro Jokers for beginners",
    description: "Beginner Jokers that pay you for playing: Green Joker, Ride the Bus, Supernova, Photograph, and simple +chips.",
    answer: "Buy Jokers that score even when your poker is messy. Green Joker and Ride the Bus add Mult for consistent habits. Supernova rewards spamming one hand. Photograph plus Hanging Chad is the first combo worth learning. Skip highly conditional Jokers until shops are readable.",
    bullets: [
      "Green Joker: +Mult if you do not discard that round.",
      "Ride the Bus: +Mult if you play no face cards — fights PhotoChad.",
      "Supernova: +Mult equal to times you played that hand this run.",
      "Fibonacci / Scary Face / Even Steven: plain chips while you learn.",
    ],
    links: ["/balatro/how-jokers-work", "/balatro/best-joker-combinations", "/balatro/strategy-for-beginners", "/tournaments"],
  }),
  launch({
    cluster: "jokers",
    query: "Best Joker combinations in Balatro",
    path: "/balatro/best-joker-combinations",
    faq: [{ q: "Best Joker combinations in Balatro", a: "A combination is two or more Jokers that share a trigger. Photograph + Hanging Chad retriggers the first face card. Blueprint copies the Joker on its right. Baron + Mime + steel cards is a late held-in-hand line. Random rares glued together are not a combo." }],
    title: "Best Joker Combinations in Balatro | GamePlat",
    h1: "Best Joker combinations in Balatro",
    description: "PhotoChad, Blueprint copies, and Baron-Mime steel lines — the combinations Balatro players actually ask about.",
    answer: "A combination is two or more Jokers that share a trigger. Photograph + Hanging Chad retriggers the first face card. Blueprint copies the Joker on its right. Baron + Mime + steel cards is a late held-in-hand line. Random rares glued together are not a combo.",
    bullets: [
      "PhotoChad: first-card retrigger on a scoring face card.",
      "Blueprint right / Brainstorm left — placement is the combo.",
      "Do not mix Ride the Bus with a face-card Photograph plan.",
      "Contest scores still need a legal, unmodded run.",
    ],
    links: ["/balatro/how-to-order-jokers", "/balatro/best-builds", "/balatro/high-card-build", "/tournaments"],
  }),
  launch({
    cluster: "jokers",
    query: "How to order Jokers in Balatro",
    path: "/balatro/how-to-order-jokers",
    faq: [{ q: "How to order Jokers in Balatro", a: "Jokers resolve left to right. Put additive chips and +Mult first, \u00d7Mult later, and sit Blueprint immediately left of the Joker you want copied (it copies the right neighbor). Brainstorm copies the left neighbor. Wrong order is why a 'good board' still scores like Ante 3." }],
    title: "How to Order Jokers in Balatro | GamePlat",
    h1: "How to order Jokers in Balatro",
    description: "Joker order is left to right. Put +chips and +Mult before ×Mult, and put Blueprint next to the Joker it should copy.",
    answer: "Jokers resolve left to right. Put additive chips and +Mult first, ×Mult later, and sit Blueprint immediately left of the Joker you want copied (it copies the right neighbor). Brainstorm copies the left neighbor. Wrong order is why a 'good board' still scores like Ante 3.",
    bullets: [
      "Left: chips and +Mult. Right: ×Mult and finishers.",
      "Blueprint: payload on its right.",
      "Brainstorm: payload on its left.",
      "Reorder in the Joker row any time — it is not locked.",
    ],
    links: ["/balatro/how-jokers-work", "/balatro/plus-mult-vs-xmult", "/balatro/best-joker-combinations", "/tournaments"],
  }),
];

const DECK_LAUNCH = [
  launch({
    cluster: "decks",
    query: "Balatro Red Deck strategy",
    path: "/balatro/red-deck",
    faq: [{ q: "Balatro Red Deck strategy", a: "Red Deck gives +1 discard every round. Use it to find the hand you scaled, or to feed discard Jokers such as Yorick. It is the correct first-win deck and a legal default for community events unless the rules say otherwise." }],
    title: "Balatro Red Deck Strategy | GamePlat",
    h1: "Balatro Red Deck strategy",
    description: "Red Deck extra discard strategy: beginner wins, discard Jokers, and a clean path into GamePlat contests.",
    answer: "Red Deck gives +1 discard every round. Use it to find the hand you scaled, or to feed discard Jokers such as Yorick. It is the correct first-win deck and a legal default for community events unless the rules say otherwise.",
    bullets: [
      "Spend the extra discard on making the planet hand, not on fishing legendaries.",
      "Pairs with Yorick (discard ×Mult) and any line that can afford throws.",
      "If you still lose, the deck is not the problem — economy and Mult are.",
    ],
    links: ["/balatro/best-deck-for-beginners", "/balatro/blue-deck", "/balatro/beginner-guide", "/tournaments"],
  }),
  launch({
    cluster: "decks",
    query: "Balatro Blue Deck strategy",
    path: "/balatro/blue-deck",
    faq: [{ q: "Balatro Blue Deck strategy", a: "Blue Deck gives an extra hand instead of an extra discard. You score more times per round and reshape less. Take it when your engine is already a 'play the hand' engine (Supernova, Green Joker) and you do not need fishing discards." }],
    title: "Balatro Blue Deck Strategy | GamePlat",
    h1: "Balatro Blue Deck strategy",
    description: "Blue Deck extra hand strategy: more scoring attempts, fewer discards. When to pick it over Red.",
    answer: "Blue Deck gives an extra hand instead of an extra discard. You score more times per round and reshape less. Take it when your engine is already a 'play the hand' engine (Supernova, Green Joker) and you do not need fishing discards.",
    bullets: [
      "Extra hand = extra chance to hit the blind without a perfect draw.",
      "Weaker with discard-scaling Jokers.",
      "Still respect interest. Extra hands do not print gold by themselves.",
    ],
    links: ["/balatro/red-deck", "/balatro/green-deck", "/balatro/best-deck-for-beginners", "/tournaments"],
  }),
  launch({
    cluster: "decks",
    query: "Balatro Yellow Deck strategy",
    path: "/balatro/yellow-deck",
    faq: [{ q: "Balatro Yellow Deck strategy", a: "Yellow Deck's extra starting gold is only an advantage if you reach the $25 interest cap sooner and buy one real engine piece. Opening every pack on Ante 1 is how Yellow Deck players still die at Ante 4." }],
    title: "Balatro Yellow Deck Strategy | GamePlat",
    h1: "Balatro Yellow Deck strategy",
    description: "Yellow Deck starts with extra money. Hit the $25 interest cap faster — do not donate it all to packs.",
    answer: "Yellow Deck's extra starting gold is only an advantage if you reach the $25 interest cap sooner and buy one real engine piece. Opening every pack on Ante 1 is how Yellow Deck players still die at Ante 4.",
    bullets: [
      "Rush $25, then shop like a miser except for the missing engine piece.",
      "Gold cards and mail-in Jokers stack with this deck's identity.",
      "A rich bad board still loses Ante 8.",
    ],
    links: ["/balatro/interest", "/balatro/make-money", "/balatro/shop", "/tournaments"],
  }),
  launch({
    cluster: "decks",
    query: "Balatro Green Deck strategy",
    path: "/balatro/green-deck",
    faq: [{ q: "Balatro Green Deck strategy", a: "Green Deck converts unused hands and discards into dollars. Play tight: beat the blind with leftover resources and you get paid. Wasteful five-hand panic dumps kill the economy identity of the deck." }],
    title: "Balatro Green Deck Strategy | GamePlat",
    h1: "Balatro Green Deck strategy",
    description: "Green Deck pays leftover hands and discards as money. Efficient rounds print interest.",
    answer: "Green Deck converts unused hands and discards into dollars. Play tight: beat the blind with leftover resources and you get paid. Wasteful five-hand panic dumps kill the economy identity of the deck.",
    bullets: [
      "Win the blind early when the score is already there.",
      "Pairs with efficient +Mult engines, not fishing simulators.",
      "Still cap interest at $25 — the deck helps you get there.",
    ],
    links: ["/balatro/blue-deck", "/balatro/make-money", "/balatro/interest", "/tournaments"],
  }),
];

const STRATEGY_LAUNCH = [
  launch({
    cluster: "strategy",
    query: "Balatro +Mult vs ×Mult",
    path: "/balatro/plus-mult-vs-xmult",
    faq: [{ q: "Balatro +Mult vs \u00d7Mult", a: "+Mult (additive) makes each hand respectably larger. \u00d7Mult (multiplicative) is what turns a stable engine into Ante 8 and contest scores. If you only have \u00d7Mult on a tiny Mult number, the product stays tiny. Get +Mult and chips first, then multiply." }],
    title: "Balatro +Mult vs ×Mult | GamePlat",
    h1: "Balatro +Mult vs ×Mult",
    description: " +Mult raises the floor. ×Mult wins Ante 8. Why 2026 beginner threads still stall without both chips and Mult.",
    answer: "+Mult (additive) makes each hand respectably larger. ×Mult (multiplicative) is what turns a stable engine into Ante 8 and contest scores. If you only have ×Mult on a tiny Mult number, the product stays tiny. Get +Mult and chips first, then multiply.",
    bullets: [
      "Ante 1–4: chips and +Mult so blinds do not kill you.",
      "Ante 5–8: ×Mult and retriggers on a real Mult pile.",
      "Plasma Deck averages chips and Mult — a special case.",
      "Order Jokers so +Mult happens before ×Mult when the text allows it.",
    ],
    links: ["/balatro/scoring", "/balatro/past-ante-4", "/balatro/how-to-order-jokers", "/tournaments"],
  }),
  launch({
    cluster: "strategy",
    query: "How does Balatro scoring work?",
    path: "/balatro/scoring",
    faq: [{ q: "How does Balatro scoring work?", a: "Each played hand starts with the poker's base chips and Mult, adds chip value from the cards, applies enhancements, then runs Jokers left to right. Planets raise the base for one hand type. That is why spamming the hand you planeted beats a random Royal Flush." }],
    title: "How Does Balatro Scoring Work? | GamePlat",
    h1: "How does Balatro scoring work?",
    description: "Balatro scoring is chips × Mult: base hand, card chips, planets, then Jokers in order. Required reading before a high-score event.",
    answer: "Each played hand starts with the poker's base chips and Mult, adds chip value from the cards, applies enhancements, then runs Jokers left to right. Planets raise the base for one hand type. That is why spamming the hand you planeted beats a random Royal Flush.",
    bullets: [
      "Level the hand you will actually play.",
      "Glass, steel, and lucky change a card's job.",
      "A GamePlat screenshot should show the final score screen.",
    ],
    links: ["/balatro/plus-mult-vs-xmult", "/balatro/how-to-play", "/balatro/how-to-order-jokers", "/tournaments"],
  }),
  launch({
    cluster: "strategy",
    query: "How to get past Ante 4",
    path: "/balatro/past-ante-4",
    faq: [{ q: "How to get past Ante 4", a: "Ante 4 is a scoring check. If you arrive with economy but no +Mult, or with Mult and no chips, the Big/Boss blinds end the run. Sell the novelty Joker. Buy chips or +Mult that trigger every hand. Interest helps only if you also score." }],
    title: "How to Get Past Ante 4 in Balatro | GamePlat",
    h1: "How to get past Ante 4",
    description: "Ante 4 is where blinds jump. You need +chips and +Mult, not another cute Joker. The same question players still ask in 2026.",
    answer: "Ante 4 is a scoring check. If you arrive with economy but no +Mult, or with Mult and no chips, the Big/Boss blinds end the run. Sell the novelty Joker. Buy chips or +Mult that trigger every hand. Interest helps only if you also score.",
    bullets: [
      "Count whether every hand actually triggers your Jokers.",
      "A skip that pays money can be better than a dead pack.",
      "Once Ante 4 is free, start looking for ×Mult before Ante 8.",
    ],
    links: ["/balatro/past-ante-8", "/balatro/strategy-for-beginners", "/balatro/plus-mult-vs-xmult", "/tournaments"],
  }),
  launch({
    cluster: "strategy",
    query: "How to get past Ante 8",
    path: "/balatro/past-ante-8",
    faq: [{ q: "How to get past Ante 8", a: "Ante 8 bosses are huge chip walls plus a rule twist. You need multiplicative scoring and a plan if the boss bans your only trick (face cards, discards, a suit). If you can farm Ante 8, you are ready to join the waitlist and submit a verified run." }],
    title: "How to Get Past Ante 8 in Balatro | GamePlat",
    h1: "How to get past Ante 8",
    description: "Ante 8 needs an ×Mult engine and a boss-blind answer. That is also the skill floor for GamePlat high-score contests.",
    answer: "Ante 8 bosses are huge chip walls plus a rule twist. You need multiplicative scoring and a plan if the boss bans your only trick (face cards, discards, a suit). If you can farm Ante 8, you are ready to join the waitlist and submit a verified run.",
    bullets: [
      "Retriggers and copy Jokers are the usual ceiling.",
      "Read the boss before the round starts. Reroll or pivot if you can.",
      "Keep seed + screenshot for GamePlat review.",
    ],
    links: ["/balatro/how-to-beat-balatro", "/balatro/best-builds", "/balatro/how-tournaments-work", "/tournaments"],
  }),
  launch({
    cluster: "strategy",
    query: "How to make money in Balatro",
    path: "/balatro/make-money",
    faq: [{ q: "How to make money in Balatro", a: "You earn from beaten blinds, interest, gold cards held to round end, and economy Jokers. The target is $25 in the bank so interest caps at $5 each round. Spending every dollar in the shop is how 'I never have money' threads start." }],
    title: "How to Make Money in Balatro | GamePlat",
    h1: "How to make money in Balatro",
    description: "Balatro money comes from blinds, interest, gold cards, and a few Jokers. Hitting $25 is the real trick.",
    answer: "You earn from beaten blinds, interest, gold cards held to round end, and economy Jokers. The target is $25 in the bank so interest caps at $5 each round. Spending every dollar in the shop is how 'I never have money' threads start.",
    bullets: [
      "Yellow Deck and gold cards accelerate the cap.",
      "Mail-in rebate and similar Jokers are extra, not a plan by themselves.",
      "Convert leftover gold into score before the Ante 8 boss in a contest.",
    ],
    links: ["/balatro/interest", "/balatro/shop", "/balatro/yellow-deck", "/tournaments"],
  }),
  launch({
    cluster: "strategy",
    query: "Balatro interest explained",
    path: "/balatro/interest",
    faq: [{ q: "Balatro interest explained", a: "Interest pays at the end of the round based on how much gold you are holding, up to $5 at $25. Vouchers can change this later. Until then, treating $25 as a sacred number is the highest-ROI beginner habit in the game \u2014 and it shows up in almost every 'how do I get past Ante 4' answer." }],
    title: "Balatro Interest Explained | GamePlat",
    h1: "Balatro interest explained",
    description: "Interest is $1 per $5 held, capped at $5 when you have $25. The quiet win condition in beginner threads.",
    answer: "Interest pays at the end of the round based on how much gold you are holding, up to $5 at $25. Vouchers can change this later. Until then, treating $25 as a sacred number is the highest-ROI beginner habit in the game — and it shows up in almost every 'how do I get past Ante 4' answer.",
    bullets: [
      "Cap: $5 per round at $25 held.",
      "Going to $0 for a pack resets the snowball.",
      "Green/Yellow decks and gold cards exist to help you hold.",
    ],
    links: ["/balatro/make-money", "/balatro/shop", "/balatro/past-ante-4", "/tournaments"],
  }),
  launch({
    cluster: "strategy",
    query: "What should I buy in the Balatro shop?",
    path: "/balatro/shop",
    faq: [{ q: "What should I buy in the Balatro shop?", a: "Buy the Joker or voucher that completes the plan you already have. If you are a Flush run, stop buying Straight planets. Packs are for the hole in the engine, not for entertainment. If nothing in the shop serves the plan, leave." }],
    title: "What Should I Buy in the Balatro Shop? | GamePlat",
    h1: "What should I buy in the Balatro shop?",
    description: "Buy the missing engine piece. Skip packs that do not serve the hand you scaled. Interest beats a random rare.",
    answer: "Buy the Joker or voucher that completes the plan you already have. If you are a Flush run, stop buying Straight planets. Packs are for the hole in the engine, not for entertainment. If nothing in the shop serves the plan, leave.",
    bullets: [
      "One good Joker > three 'maybe' Jokers.",
      "Planet the hand you play, not the hand you wish you had.",
      "Reroll only when you are one piece away — see the reroll guide.",
    ],
    links: ["/balatro/reroll", "/balatro/interest", "/balatro/best-jokers-for-beginners", "/tournaments"],
  }),
  launch({
    cluster: "strategy",
    query: "When should I reroll in Balatro?",
    path: "/balatro/reroll",
    faq: [{ q: "When should I reroll in Balatro?", a: "Rerolls cost more each time in a shop. Spend them when the board is one copy, one retrigger, or one +Mult piece from working. Rerolling because the shop is 'unfun' is how interest dies. Skip tags can beat a greedy reroll." }],
    title: "When Should I Reroll in Balatro? | GamePlat",
    h1: "When should I reroll in Balatro?",
    description: "Reroll when you are one Joker away from the engine. Do not reroll boredom on Ante 2 — rerolls get expensive.",
    answer: "Rerolls cost more each time in a shop. Spend them when the board is one copy, one retrigger, or one +Mult piece from working. Rerolling because the shop is 'unfun' is how interest dies. Skip tags can beat a greedy reroll.",
    bullets: [
      "First reroll is sometimes correct. The fifth almost never is.",
      "If you cannot name the Joker you are hunting, do not reroll.",
      "Contest runs still have to be unmodded — no shop editors.",
    ],
    links: ["/balatro/shop", "/balatro/make-money", "/balatro/how-to-order-jokers", "/tournaments"],
  }),
  launch({
    cluster: "strategy",
    query: "Best Balatro builds",
    path: "/balatro/best-builds",
    faq: [{ q: "Best Balatro builds", a: "A build is a hand plus the Jokers that pay that hand. Flush is the default first-win shape. High-card PhotoChad scores the card, not the poker. Pair + Supernova is honest and strong. Pick one, planet it, and stop mixing. Fancy Baron-Mime steel is a later cluster page." }],
    title: "Best Balatro Builds | GamePlat",
    h1: "Best Balatro builds",
    description: "Flush, high-card PhotoChad, and pair/Supernova are the builds that teach Balatro. Late-game Baron lines come after.",
    answer: "A build is a hand plus the Jokers that pay that hand. Flush is the default first-win shape. High-card PhotoChad scores the card, not the poker. Pair + Supernova is honest and strong. Pick one, planet it, and stop mixing. Fancy Baron-Mime steel is a later cluster page.",
    bullets: [
      "Flush: suited decks, wilds, Checkered.",
      "High card: Photograph engines.",
      "Pair: Supernova and simple +Mult.",
    ],
    links: ["/balatro/flush-build", "/balatro/high-card-build", "/balatro/pair-build", "/tournaments"],
  }),
  launch({
    cluster: "strategy",
    query: "Balatro flush build",
    path: "/balatro/flush-build",
    faq: [{ q: "Balatro flush build", a: "Flush wins first runs because five suited cards are easier to see than a miracle straight. Planet Flush, take wilds and suit Jokers, and stop playing off-suit 'better' poker. Checkered Deck is a Flush specialist." }],
    title: "Balatro Flush Build | GamePlat",
    h1: "Balatro flush build",
    description: "How to commit to Flush in Balatro: suits, wilds, planets, and which decks make it easy.",
    answer: "Flush wins first runs because five suited cards are easier to see than a miracle straight. Planet Flush, take wilds and suit Jokers, and stop playing off-suit 'better' poker. Checkered Deck is a Flush specialist.",
    bullets: [
      "One suit focus plus wilds beats rainbow dreaming.",
      "Ancient Joker wants suit flexibility — know the conflict.",
      "Legal in GamePlat standard-deck events.",
    ],
    links: ["/balatro/best-builds", "/balatro/best-deck-for-beginners", "/balatro/scoring", "/tournaments"],
  }),
  launch({
    cluster: "strategy",
    query: "Balatro high-card build",
    path: "/balatro/high-card-build",
    faq: [{ q: "Balatro high-card build", a: "If Photograph, Hanging Chad, and face cards are doing the damage, you do not need a flush. Play the smallest legal hand that still triggers the first card. This is the combo people mean by PhotoChad." }],
    title: "Balatro High-Card Build | GamePlat",
    h1: "Balatro high-card build",
    description: "High-card builds score the Jokers, not the poker hand. Photograph and retriggers live here.",
    answer: "If Photograph, Hanging Chad, and face cards are doing the damage, you do not need a flush. Play the smallest legal hand that still triggers the first card. This is the combo people mean by PhotoChad.",
    bullets: [
      "Conflicts with Ride the Bus (no faces).",
      "Order retriggers next to Photograph.",
      "Great teaching engine for contest-level scoring later.",
    ],
    links: ["/balatro/best-joker-combinations", "/balatro/best-builds", "/balatro/how-to-order-jokers", "/tournaments"],
  }),
  launch({
    cluster: "strategy",
    query: "Balatro pair build",
    path: "/balatro/pair-build",
    faq: [{ q: "Balatro pair build", a: "A pair build works when you planet Pair and take Jokers that pay you for repeating it (Supernova, leftover +Mult). It is less flashy than Flush Five and much more realistic for Ante 8 than forcing four of a kind on a thick deck." }],
    title: "Balatro Pair Build | GamePlat",
    h1: "Balatro pair build",
    description: "Pair plus Supernova is an honest Balatro engine: play the same hand, stack +Mult, planet Pair.",
    answer: "A pair build works when you planet Pair and take Jokers that pay you for repeating it (Supernova, leftover +Mult). It is less flashy than Flush Five and much more realistic for Ante 8 than forcing four of a kind on a thick deck.",
    bullets: [
      "Planet Pair. Stop buying Straight planets 'just in case.'",
      "Abandoned Deck four-oak is a different page — do not mix the advice.",
      "Easy to explain in a community event screenshot.",
    ],
    links: ["/balatro/best-builds", "/balatro/best-jokers-for-beginners", "/balatro/scoring", "/tournaments"],
  }),
];

const TOURNAMENT_LAUNCH = [
  launch({
    cluster: "tournament",
    query: "How do Balatro tournaments work?",
    path: "/balatro/how-tournaments-work",
    title: "How Do Balatro Tournaments Work? | GamePlat",
    h1: "How do Balatro tournaments work?",
    description: "GamePlat Balatro tournaments: free waitlist, official game, verified scores, leaderboard points. Create an account and register.",
    intent: "transactional",
    answer: "You play Balatro on an official copy. GamePlat runs the community contest: join the waitlist, create a free account, submit a result with proof when the event is live. Wins are verified before they hit the leaderboard (3 points per verified win). Yorick chat is coaching, not a score drop box.",
    bullets: [
      "Step 1: free account at /login.",
      "Step 2: waitlist at /tournaments.",
      "Step 3: play fair, screenshot the score, submit on the event page.",
      "No mods, no multi-accounts. See fair play.",
    ],
    faq: [
      { q: "Is it free?", a: "Yes. Waitlist and contest entry are free. Paid Yorick plans are optional coaching." },
      { q: "Is there a date?", a: "Yes. The GamePlat Balatro Community Challenge is December 12, 2026. Join the waitlist on /tournaments. Waitlist size stays private." },
    ],
    links: ["/tournaments", "/login", "/balatro/fair-play", "/leaderboard"],
    secondaryCta: ACCOUNT,
  }),
];

const ACADEMY_QUEUE = [
  queued("academy", "what is balatro", "what-is-balatro", "What Is Balatro? | GamePlat", "Poker-themed single-player roguelike by LocalThunk. GamePlat is the community contest layer."),
  queued("academy", "first winning run balatro", "first-winning-run", "Your First Balatro Win | GamePlat", "White stake, Red Deck, one scaled hand, two honest Jokers."),
  queued("academy", "balatro blinds and antes", "blinds-and-antes", "Blinds and Antes Explained | GamePlat", "Small, Big, Boss each ante. The boss is a rule change, not just a bigger number."),
  queued("academy", "tarot cards balatro", "tarot-cards", "Tarot Cards in Balatro | GamePlat", "Enhance, copy, destroy, convert. The deck-sculpting kit."),
  queued("academy", "planet cards balatro", "planet-cards", "Planet Cards in Balatro | GamePlat", "Level one poker hand. Only planet the hand you will spam."),
  queued("academy", "spectral cards balatro", "spectral-cards", "Spectral Cards in Balatro | GamePlat", "Powerful, often costly. Separate from GamePlat contest rules."),
  queued("academy", "glass cards balatro", "glass-cards", "Glass Cards in Balatro | GamePlat", "x2 then a chance to break. Retrigger only when the math is worth it."),
  queued("academy", "steel cards balatro", "steel-cards", "Steel Cards in Balatro | GamePlat", "xMult while held. Baron/Mime lines care a lot."),
  queued("academy", "gold cards balatro", "gold-cards", "Gold Cards in Balatro | GamePlat", "Money if held at end of round. Economy first."),
  queued("academy", "lucky cards balatro", "lucky-cards", "Lucky Cards in Balatro | GamePlat", "Random chips/mult/money. Lucky Cat is the scaling payoff."),
  queued("academy", "balatro consumables explained", "consumables", "Balatro Consumables Explained | GamePlat", "Tarot, planet, and spectral roles in one page after launch demand."),
];

const JOKER_QUEUE = [
  ["blueprint joker balatro", "blueprint", "Blueprint Joker Guide | GamePlat", "Copies the Joker to its right. Placement is the whole guide."],
  ["brainstorm joker balatro", "brainstorm", "Brainstorm Joker Guide | GamePlat", "Copies the Joker to its left. Mirror Blueprint."],
  ["photograph joker balatro", "photograph", "Photograph Joker | GamePlat", "xMult on the first played face card. Retrigger magnet."],
  ["hanging chad balatro", "hanging-chad", "Hanging Chad Joker | GamePlat", "Retriggers the first card played. PhotoChad's other half."],
  ["baron joker balatro", "baron", "Baron Joker | GamePlat", "xMult for Kings held in hand. Late-game steel partner."],
  ["mime joker balatro", "mime", "Mime Joker | GamePlat", "Retriggers held-in-hand cards. Baron's partner."],
  ["yorick joker balatro", "yorick-joker", "Yorick Joker in Balatro | GamePlat", "xMult from discarding. Also GamePlat's coach name."],
  ["triboulet joker balatro", "triboulet", "Triboulet Joker | GamePlat", "Legendary face-card xMult. Needs retriggers."],
  ["cavendish joker balatro", "cavendish", "Cavendish Joker | GamePlat", "The banana that stays after Gros Michel extincts."],
  ["gros michel balatro", "gros-michel", "Gros Michel Joker | GamePlat", "Early xMult that can go extinct. Plan the replacement."],
  ["green joker balatro", "green-joker", "Green Joker | GamePlat", "Beginner +Mult for not discarding."],
  ["ride the bus joker", "ride-the-bus", "Ride the Bus Joker | GamePlat", "+Mult if you play no faces. Fights Photograph."],
  ["supernova joker balatro", "supernova", "Supernova Joker | GamePlat", "+Mult equal to times you played that hand."],
  ["burglar joker balatro", "burglar", "Burglar Joker | GamePlat", "Trades discards for extra hands."],
  ["stuntman joker balatro", "stuntman", "Stuntman Joker | GamePlat", "Huge chips, smaller hand size."],
  ["baseball card joker", "baseball-card", "Baseball Card Joker | GamePlat", "xMult per uncommon Joker."],
  ["campfire joker balatro", "campfire", "Campfire Joker | GamePlat", "xMult when you sell. A sell engine."],
  ["ancient joker balatro", "ancient-joker", "Ancient Joker | GamePlat", "xMult for a rotating suit."],
  ["vampire joker balatro", "vampire", "Vampire Joker | GamePlat", "Eats enhancements for xMult."],
  ["idol joker balatro", "idol", "The Idol Joker | GamePlat", "xMult for a specific rank and suit."],
  ["fibonacci joker balatro", "fibonacci", "Fibonacci Joker | GamePlat", "Chip Joker on Ace, 2, 3, 5, 8. Beginner chips."],
  ["constellation joker balatro", "constellation", "Constellation Joker | GamePlat", "xMult per planet used. Planet-commit payoff."],
  ["hologram joker balatro", "hologram", "Hologram Joker | GamePlat", "xMult as you add cards to the deck."],
  ["bull joker balatro", "bull", "Bull Joker | GamePlat", "Chips from current gold. Economy scoring."],
  ["todo list joker balatro", "todo-list", "To Do List Joker | GamePlat", "Money for playing a listed poker hand."],
  ["joker collection balatro", "joker-index", "Joker Database Index | GamePlat", "Hub index for individual Joker pages as they pass review."],
].map(([query, slug, title, outline]) => queued("jokers", query, slug, title, outline));

const DECK_QUEUE = [
  ["black deck balatro", "black-deck", "Black Deck Strategy | GamePlat", "Extra Joker slot, fewer hands. Not a first-win deck."],
  ["magic deck balatro", "magic-deck", "Magic Deck Strategy | GamePlat", "Start with Crystal Ball and Fool. Consumable identity."],
  ["nebula deck balatro", "nebula-deck", "Nebula Deck Strategy | GamePlat", "Telescope, smaller consumable slot. Planet focus."],
  ["ghost deck balatro", "ghost-deck", "Ghost Deck Strategy | GamePlat", "Spectrals in the shop. High variance."],
  ["abandoned deck balatro", "abandoned-deck", "Abandoned Deck Strategy | GamePlat", "No face cards. Four/five of a kind lines."],
  ["checkered deck balatro", "checkered-deck", "Checkered Deck Strategy | GamePlat", "Two suits. Flush is the point."],
  ["zodiac deck balatro", "zodiac-deck", "Zodiac Deck Strategy | GamePlat", "Tarot/planet/overstock identity. Shop density."],
  ["painted deck balatro", "painted-deck", "Painted Deck Strategy | GamePlat", "Larger hand, fewer Joker slots."],
  ["anaglyph deck balatro", "anaglyph-deck", "Anaglyph Deck Strategy | GamePlat", "Double tags after bosses."],
  ["plasma deck balatro", "plasma-deck", "Plasma Deck Strategy | GamePlat", "Balances chips and Mult. Chip strategies become scores."],
  ["erratic deck balatro", "erratic-deck", "Erratic Deck Strategy | GamePlat", "Randomized ranks/suits. Adapt or skip until later."],
].map(([query, slug, title, outline]) => queued("decks", query, slug, title, outline));

const STRATEGY_QUEUE = [
  ["straight build balatro", "straight-build", "Straight Build | GamePlat", "Sequenced ranks. Four Fingers and Shortcut change the math."],
  ["full house build balatro", "full-house-build", "Full House Build | GamePlat", "Sturdy mid-game hand while you look for a better engine."],
  ["four of a kind build balatro", "four-oak-build", "Four of a Kind Build | GamePlat", "Needs copies or Abandoned Deck."],
  ["photochad build balatro", "photochad-build", "PhotoChad Build | GamePlat", "Photograph + Hanging Chad deep dive after the launch combo page."],
  ["baron mime steel build", "baron-mime-steel", "Baron Mime Steel Build | GamePlat", "Late held-in-hand scoring. Not run one."],
  ["plasma flush build", "plasma-flush", "Plasma Flush Build | GamePlat", "Plasma averaging plus Flush chips."],
  ["scaling into late antes", "scale-late-antes", "Scaling into Late Antes | GamePlat", "From +Mult to retriggers to ×Mult."],
  ["boss blinds balatro", "boss-blinds", "Boss Blinds Guide | GamePlat", "Each boss bans a tactic. Build a backup."],
  ["the wall boss blind", "the-wall", "The Wall Boss Blind | GamePlat", "Huge chip requirement. Scoring-check boss."],
  ["the hook boss blind", "the-hook", "The Hook Boss Blind | GamePlat", "Discards random cards. Fragile five-card setups suffer."],
  ["when to skip blinds balatro", "skip-blinds", "When to Skip Blinds | GamePlat", "Skip for tags and tempo, not out of fear."],
  ["voucher priority balatro", "vouchers", "Voucher Priority | GamePlat", "Overstock, reroll, interest, hand size."],
  ["balatro seeds explained", "seeds", "Balatro Seeds Explained | GamePlat", "Keep the seed for proof. Seeded practice ≠ contest submit unless allowed."],
].map(([query, slug, title, outline]) => queued("strategy", query, slug, title, outline));

const TOURNAMENT_QUEUE = [
  queued("tournament", "balatro community challenge", "community-challenge", "Balatro Community Challenge | GamePlat", "Free 64-player GamePlat event. Waitlist for the date.", "transactional"),
  queued("tournament", "how to join balatro contest", "how-to-join", "How to Join the Contest | GamePlat", "Account, waitlist, official game, verified screenshot.", "transactional"),
  queued("tournament", "balatro tournament fair play", "fair-play", "Fair Play in Balatro Contests | GamePlat", "No mods, no trainers, one account. Admin review is final."),
  queued("tournament", "how gameplat leaderboard works", "leaderboard-explained", "How the Leaderboard Works | GamePlat", "Verified wins are worth 3 points. Pending results are not live."),
  queued("tournament", "how to submit balatro score", "submit-your-score", "How to Submit a Score | GamePlat", "Tournament page form plus proof. Yorick chat is not a drop box.", "transactional"),
  queued("tournament", "when is the next balatro tournament", "when-is-the-next-tournament", "When Is the Next Tournament? | GamePlat", "December 12, 2026. Join the waitlist."),
  queued("tournament", "create gameplat account", "create-an-account", "Create a GamePlat Account | GamePlat", "Free account required to register and submit.", "transactional"),
  queued("tournament", "gameplat event alerts", "event-alerts", "Event Alerts and Newsletter | GamePlat", "Subscriber list for dates and results. Separate from waitlist.", "transactional"),
  queued("tournament", "gameplat tournament rules explained", "tournament-rules", "Contest Rules Explained | GamePlat", "Plain-language eligibility, proof, and bans."),
];

export const PAGES = [
  ...ACADEMY_LAUNCH,
  ...ACADEMY_QUEUE,
  ...JOKER_LAUNCH,
  ...JOKER_QUEUE,
  ...DECK_LAUNCH,
  ...DECK_QUEUE,
  ...STRATEGY_LAUNCH,
  ...STRATEGY_QUEUE,
  ...TOURNAMENT_LAUNCH,
  ...TOURNAMENT_QUEUE,
];

export function pageByPath(path) {
  const clean = (path || "/").replace(/\/$/, "") || "/";
  return PAGES.find((p) => p.path === clean);
}

export function pageBySlug(slug) {
  return pageByPath(`/balatro/${slug}`);
}

export function launchPages() {
  return PAGES.filter((p) => p.status === "launch");
}

export function queuedPages() {
  return PAGES.filter((p) => p.status === "queued");
}

export function pagesInCluster(id) {
  return PAGES.filter((p) => p.cluster === id);
}

export function launchInCluster(id) {
  return PAGES.filter((p) => p.cluster === id && p.status === "launch");
}

export function relatedLaunchPages(page, limit = 4) {
  if (!page) return [];
  const wanted = new Set(page.links || []);
  return PAGES.filter(
    (p) => p.path !== page.path && p.status === "launch" && (wanted.has(p.path) || p.cluster === page.cluster),
  ).slice(0, limit);
}

export const PLATFORM_META = {
  "/": {
    path: "/",
    title: "Balatro Tournaments and Community | GamePlat",
    description: "GamePlat is the Balatro tournament community and Yorick strategy coach. Join the waitlist, create a free account, and compete in the next Community Challenge.",
    indexable: true,
  },
  "/balatro": {
    path: "/balatro",
    title: "Balatro Hub: Tournaments, Rankings, Strategy | GamePlat",
    description: "Tournaments, rankings, fair-play rules, and 30 launch Balatro guides. Join the GamePlat Community Challenge waitlist.",
    indexable: true,
  },
  "/tournaments": {
    path: "/tournaments",
    title: "Balatro Community Challenge Waitlist | GamePlat",
    description: "Free Balatro community tournament waitlist. Register your handle now and we will notify you when the 64-player Community Challenge opens.",
    indexable: true,
  },
};

export const CATALOG_STATS = {
  questions: PAGES.length,
  launch: PAGES.filter((p) => p.status === "launch").length,
  queued: PAGES.filter((p) => p.status === "queued").length,
  byCluster: Object.fromEntries(CLUSTERS.map((c) => [c.id, PAGES.filter((p) => p.cluster === c.id).length])),
};
